<template>
  <div class="container-fluid py-4">
    <!-- En-tête -->
    <div class="page-header mb-4">
      <div class="header-content">
        <div class="d-flex align-items-center gap-3">
          <router-link to="/mes-devoirs" class="btn-back-modern">
            <i class="fa fa-arrow-left"></i>
          </router-link>
          <div>
            <h2 class="header-title">Modifier un devoir</h2>
            <p class="header-subtitle">Mettez à jour les informations de votre devoir</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Chargement -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="loading-text mt-3">Chargement...</p>
    </div>

    <!-- Formulaire -->
    <div v-else class="modern-card">
      <div class="card-body">
        <Form
          ref="devoirForm"
          @submit="updateDevoir"
          :validation-schema="devoirSchema"
        >
          <div class="row g-4">
            <!-- Date de soumission -->
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label-modern">
                  <i class="fa fa-calendar me-2"></i>
                  Date de soumission
                </label>
                <input
                  type="text"
                  class="form-control-modern"
                  :value="formatDate(devoir?.dateSoumission)"
                  disabled
                />
              </div>
            </div>

            <!-- Composition -->
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label-modern">
                  <i class="fa fa-book me-2"></i>
                  Composition
                </label>
                <input
                  type="text"
                  class="form-control-modern"
                  :value="devoir?.composition?.titre"
                  disabled
                />
              </div>
            </div>
          </div>

          <!-- Détails de la composition -->
          <div v-if="devoir?.composition" class="detail-card mt-4">
            <div class="detail-header">
              <i class="fa fa-info-circle me-2"></i>
              Détails de la composition
            </div>
            <div class="detail-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <div class="detail-item">
                    <span class="detail-label">Matière</span>
                    <span class="detail-value">{{ devoir.composition.filiereNiveauMatiere?.matiere?.libelle }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="detail-item">
                    <span class="detail-label">Filière</span>
                    <span class="detail-value">{{ devoir.composition.filiereNiveauMatiere?.filiere?.libelle }}</span>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="detail-item">
                    <span class="detail-label">Niveau</span>
                    <span class="detail-value">{{ devoir.composition.filiereNiveauMatiere?.niveau?.libelle }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Fichier actuel -->
          <div class="file-current-section mt-4">
            <div class="file-current-header">
              <i class="fa fa-file-pdf-o me-2 text-danger"></i>
              Fichier actuel
            </div>
            <div class="file-current-body">
              <div class="file-current-info">
                <i class="fa fa-paperclip me-2"></i>
                <span>{{ devoir?.fichierNom }}</span>
              </div>
              <div class="file-current-actions">
                <button
                  type="button"
                  @click="viewCurrentFile"
                  class="btn-file-action btn-view-file"
                >
                  <i class="fa fa-eye me-1"></i>
                  Voir le fichier
                </button>
              </div>
            </div>
          </div>

          <!-- Upload nouveau fichier -->
          <div class="upload-section mt-4">
            <label class="form-label-modern">
              <i class="fa fa-cloud-upload me-2"></i>
              Remplacer par un nouveau fichier PDF
            </label>
            
            <div class="upload-area" @click="triggerFileInput" :class="{ 'has-file': selectedFile }">
              <input
                type="file"
                ref="fileInput"
                @change="handleFileUpload"
                accept=".pdf"
                style="display: none;"
              />
              <div v-if="!selectedFile" class="upload-placeholder">
                <i class="fa fa-cloud-upload fa-3x text-primary mb-3"></i>
                <p class="mb-2"><strong>Cliquez pour sélectionner un nouveau fichier PDF</strong></p>
                <p class="text-muted small">ou glissez-déposez votre fichier ici</p>
                <p class="text-muted small">Taille maximale : 20 Mo</p>
              </div>
              <div v-else class="file-preview">
                <i class="fa fa-file-pdf-o fa-3x text-danger mb-2"></i>
                <p class="mb-1"><strong>{{ selectedFile.name }}</strong></p>
                <p class="text-muted small">{{ formatFileSize(selectedFile.size) }}</p>
                <button 
                  type="button" 
                  class="btn btn-sm btn-danger mt-2"
                  @click.stop="removeFile"
                >
                  <i class="fa fa-trash me-1"></i> Supprimer
                </button>
              </div>
            </div>
          </div>

          <!-- Boutons d'action -->
          <div class="action-buttons mt-4">
            <button
              class="btn-action-primary"
              type="submit"
              :disabled="isSubmitting || !selectedFile || !canModify"
            >
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fa fa-save me-2"></i>
              {{ isSubmitting ? 'Modification en cours...' : 'Enregistrer les modifications' }}
            </button>
            <router-link
              to="/mes-devoirs"
              class="btn-action-secondary"
            >
              <i class="fa fa-times me-2"></i> Annuler
            </router-link>
          </div>
        </Form>
      </div>
    </div>

    <div v-if="!isLoading && !canModify" class="alert alert-danger alert-modern mb-4">
  <div class="alert-icon">
    <i class="fa fa-exclamation-triangle"></i>
  </div>
  <div class="alert-content">
    <h5 class="alert-title">Modification impossible</h5>
    <p class="alert-message">{{ modifyCheckMessage || 'Ce devoir ne peut plus être modifié car il a déjà été corrigé.' }}</p>
  </div>
</div>

    <!-- Modal PDF -->
    <div v-if="showPdfModal" class="modal-overlay" @click.self="closePdfModal">
      <div class="modal-dialog-pdf">
        <div class="modal-content-modern">
          <div class="modal-header-pdf">
            <h5 class="modal-title">
              <i class="fa fa-file-pdf-o me-2"></i>{{ currentPdfTitle }}
            </h5>
            <button type="button" class="btn-close-modal" @click="closePdfModal">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <div class="modal-body-pdf">
            <iframe :src="currentPdfUrl" class="pdf-iframe" title="Visualiseur PDF"></iframe>
          </div>
          <div class="modal-footer-pdf">
            <a :href="currentPdfUrl" target="_blank" class="btn-modal-action">
              <i class="fa fa-external-link me-2"></i>
              Ouvrir dans un nouvel onglet
            </a>
            <button type="button" class="btn-modal-secondary" @click="closePdfModal">Fermer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { success, error } from "../../utils/utils";
import { useRouter, useRoute } from "vue-router";
import ApiService from "../../services/ApiService";
import * as Yup from "yup";

export default defineComponent({
  name: "EditDevoir",
  components: {
    Form,
  },
  setup() {
    const devoirSchema = Yup.object().shape({
      fichier: Yup.mixed().nullable(),
    });

    const route = useRoute();
    const router = useRouter();
    const devoirForm = ref<any>(null);
    const fileInput = ref<any>(null);
    const devoir = ref<any>(null);
    const selectedFile = ref<File | null>(null);
    const isSubmitting = ref(false);
    const isLoading = ref(true);
    const showPdfModal = ref(false);
    const currentPdfUrl = ref('');
    const currentPdfTitle = ref('');
    const canModify = ref(true);
    const modifyCheckMessage = ref('');

    onMounted(() => {
      const devoirId = route.params.id;
      if (devoirId) {
        fetchDevoir(devoirId as string);
      }
    });

    const checkCanModify = async (id: string) => {
  try {
    const response = await ApiService.get(`/devoirs/${id}/can-modify`);
    if (response.data.code === 200) {
      canModify.value = response.data.data.canModify;
      modifyCheckMessage.value = response.data.data.reason || '';
      
      if (!canModify.value) {
        error(modifyCheckMessage.value || "Ce devoir ne peut plus être modifié");
        // Rediriger après 2 secondes
        setTimeout(() => {
          router.push('/mes-devoirs');
        }, 2000);
      }
    }
  } catch (err) {
    console.error("Erreur vérification modification:", err);
  }
};

    const fetchDevoir = async (id: string) => {
  isLoading.value = true;
  try {
    const response = await ApiService.get(`/devoirs/${id}`);
    devoir.value = response.data.data;
    
    // ✅ Vérifier si le devoir peut être modifié
    await checkCanModify(id);
    
  } catch (err) {
    console.error("Erreur lors du chargement du devoir", err);
    error("Impossible de charger le devoir");
    router.push('/mes-devoirs');
  } finally {
    isLoading.value = false;
  }
};

    const formatDate = (date: string | Date) => {
      if (!date) return '';
      return new Date(date).toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatFileSize = (bytes: number) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    // const getFileUrl = (filePath: string) => {
    //   if (!filePath) return '';
    //   const cleanPath = filePath.replace(/^uploads\//, '');
    //   return `${API_BASE_URL}/${cleanPath}`;
    // };

    const viewCurrentFile = () => {
      if (!devoir.value?.fichierUrl) return;
      // currentPdfUrl.value = getFileUrl(devoir.value.fichierUrl);
      currentPdfTitle.value = devoir.value.fichierNom;
      showPdfModal.value = true;
      document.body.style.overflow = 'hidden';
    };

    const closePdfModal = () => {
      showPdfModal.value = false;
      currentPdfUrl.value = '';
      currentPdfTitle.value = '';
      document.body.style.overflow = '';
    };

    const triggerFileInput = () => {
      if (!selectedFile.value) {
        fileInput.value?.click();
      }
    };

    const handleFileUpload = async (event: any) => {
      const file = event.target.files[0];
      if (!file) return;

      if (file.type !== 'application/pdf') {
        error("Veuillez sélectionner un fichier PDF valide");
        return;
      }

      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        error("Le fichier ne doit pas dépasser 20 Mo");
        return;
      }

      selectedFile.value = file;
      await devoirForm.value?.setFieldValue('fichier', file);
    };

    const removeFile = () => {
      selectedFile.value = null;
      if (fileInput.value) {
        fileInput.value.value = '';
      }
      devoirForm.value?.setFieldValue('fichier', null);
    };


    const updateDevoir = async () => {
      if (!canModify.value) {
        error("Ce devoir ne peut plus être modifié");
        return;
      }

      if (!selectedFile.value) {
        error("Veuillez sélectionner un nouveau fichier PDF");
        return;
      }

      isSubmitting.value = true;

      try {
        const formData = new FormData();
        formData.append('fichier', selectedFile.value);

        // ✅ CORRECTION : Utiliser /modifier au lieu de /devoirs/:id directement
        const response = await ApiService.put(
          `/devoirs/${devoir.value.id}/modifier`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );

        if (response.data.code === 200) {
          success("Devoir modifié avec succès !");
          router.push('/mes-devoirs');
        }
      } catch (err: any) {
        console.error("Erreur lors de la modification:", err);
        if (err.response?.data?.message) {
          error(err.response.data.message);
        } else {
          error("Impossible de modifier le devoir");
        }
      } finally {
        isSubmitting.value = false;
      }
    };


    return {
      devoirSchema,
      devoirForm,
      fileInput,
      devoir,
      selectedFile,
      isSubmitting,
      isLoading,
      showPdfModal,
      currentPdfUrl,
      currentPdfTitle,
      formatDate,
      formatFileSize,
      viewCurrentFile,
      closePdfModal,
      triggerFileInput,
      handleFileUpload,
      removeFile,
      updateDevoir,
      canModify,
      modifyCheckMessage,
    };
  },
});
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 1.75rem;
  color: white;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-back-modern {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-back-modern:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateX(-4px);
  color: white;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.header-subtitle {
  margin: 0.375rem 0 0 0;
  opacity: 0.9;
  font-size: 0.875rem;
}

.loading-text {
  color: #6b7280;
  font-size: 1rem;
}

.modern-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.card-body {
  padding: 2rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label-modern {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.form-control-modern {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background: #f9fafb;
}

.form-control-modern:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.form-control-modern:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.detail-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.detail-header {
  padding: 1rem 1.25rem;
  background: linear-gradient(to bottom, #fafafa, #ffffff);
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
}

.detail-body {
  padding: 1.25rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.detail-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.file-current-section {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  overflow: hidden;
}

.file-current-header {
  padding: 1rem 1.25rem;
  background: linear-gradient(to bottom, #dbeafe, #eff6ff);
  border-bottom: 1px solid #bfdbfe;
  font-weight: 600;
  color: #1e40af;
  display: flex;
  align-items: center;
}

.file-current-body {
  padding: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.file-current-info {
  display: flex;
  align-items: center;
  color: #1e40af;
  font-weight: 500;
  font-size: 0.875rem;
}

.file-current-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-file-action {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.btn-view-file {
  background: #667eea;
  color: white;
}

.btn-view-file:hover {
  background: #5568d3;
  transform: translateY(-1px);
}

.alert-modern {
  display: flex;
  align-items: start;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid;
  background: #fef2f2;
  border-color: #fecaca;
}

.alert-danger.alert-modern {
  background: #fef2f2;
  border-color: #fecaca;
}

.alert-icon {
  width: 40px;
  height: 40px;
  background: #fee2e2;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.alert-danger .alert-icon {
  color: #dc2626;
  font-size: 1.25rem;
}

.alert-content {
  flex: 1;
}

.alert-title {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #991b1b;
}

.alert-message {
  margin: 0;
  font-size: 0.875rem;
  color: #b91c1c;
  line-height: 1.5;
}

.upload-section {
  display: flex;
  flex-direction: column;
}

.upload-area {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 2.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #f8fafc;
  margin-top: 0.5rem;
}

.upload-area:hover {
  border-color: #667eea;
  background: #f0f0ff;
}

.upload-area.has-file {
  border-color: #10b981;
  background: #f0fdf4;
  cursor: default;
}

.upload-placeholder {
  color: #64748b;
}

.file-preview {
  color: #1e293b;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-action-primary {
  flex: 1;
  min-width: 200px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  color: white;
  padding: 0.875rem 1.5rem;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-action-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-action-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-action-secondary {
  flex: 1;
  min-width: 200px;
  background: #f3f4f6;
  border: none;
  color: #374151;
  padding: 0.875rem 1.5rem;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.btn-action-secondary:hover {
  background: #e5e7eb;
  color: #374151;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-dialog-pdf {
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
}

.modal-content-modern {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header-pdf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.btn-close-modal {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.25rem;
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.3);
}

.modal-body-pdf {
  padding: 0;
  flex: 1;
  overflow: hidden;
}

.pdf-iframe {
  width: 100%;
  height: 70vh;
  border: none;
}

.modal-footer-pdf {
  display: flex;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  background: #fafbfc;
  border-top: 1px solid #e5e7eb;
}

.btn-modal-action {
  background: #667eea;
  color: white;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.btn-modal-action:hover {
  background: #5568d3;
  transform: translateY(-1px);
  color: white;
}

.btn-modal-secondary {
  background: #f3f4f6;
  color: #374151;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-modal-secondary:hover {
  background: #e5e7eb;
}

@media (max-width: 768px) {
  .page-header {
    padding: 1.25rem;
  }

  .header-title {
    font-size: 1.25rem;
  }

  .card-body {
    padding: 1.5rem;
  }

  .upload-area {
    padding: 2rem;
  }

  .action-buttons {
    flex-direction: column;
  }

  .btn-action-primary,
  .btn-action-secondary {
    width: 100%;
  }

  .file-current-body {
    flex-direction: column;
    align-items: flex-start;
  }

  .pdf-iframe {
    height: 60vh;
  }
}
</style>